import { values } from 'lodash-es';
import pRetry from 'p-retry';
import { captureLogger as logger } from './configure/logger-facade';
import { db } from './db';
import { getSafeErrorFields } from './errors';
import {
  countDrainableRetainedChunks,
  getCurrentRecordingRow,
  getPipelineMode,
  getRecordingProgress,
  hasDrainableRetainedChunks,
  isRecordingUploadComplete,
  listRecordingRows,
  peekRecordingProgress,
  peekRecordingUploadComplete,
  type RecordingProgress,
  type RecordingRow,
} from './persistenceV2';
import { type Recording } from './types';
import { useSessionRecordingStore } from './useSessionRecordingStore/useSessionRecordingStore';
import {
  isRetryDrainActive,
  pokeRetryDrain,
} from './useSessionRecordingStore/v2/retryDrainV2';
export { hasInFlightChunkUploadsForSession } from './useSessionRecordingStore/v2/persistRegistry';

type CurrentRecording =
  | { world: 'ledger'; row: RecordingRow }
  | { world: 'legacy'; legacy: Recording }
  | { world: 'none' };

interface UploadProgressState {
  isComplete: boolean;
  progress: number;
}

const startedAt = (value: Date | string | undefined): number => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(time) ? time : 0;
};

/**
 * The session's newest ledger row from either source: a row the store never
 * accepted — or can no longer read — still lives in this page's mirror, and
 * missing it would report the recording as absent while chunks upload.
 */
const currentLedgerRow = async (sessionId: string): Promise<RecordingRow | undefined> => {
  const stored = await getCurrentRecordingRow(sessionId).catch(() => undefined);
  const mirrored = listRecordingRows()
    .filter((row) => row.sessionId === sessionId)
    .sort((a, b) => b.createdAt - a.createdAt)[0];
  if (!stored || !mirrored) return stored ?? mirrored;
  return stored.createdAt >= mirrored.createdAt ? stored : mirrored;
};

/** Mirror first, matching the upload path; recovery-drained rows fall through. */
const ledgerProgress = async (row: RecordingRow): Promise<RecordingProgress> =>
  peekRecordingProgress(row) ?? (await getRecordingProgress(row));

const isLedgerUploadComplete = async (row: RecordingRow): Promise<boolean> =>
  peekRecordingUploadComplete(row) ?? (await isRecordingUploadComplete(row));

/** Reads legacy first so a migration handoff cannot temporarily hide a recording. */
const resolveCurrentRecording = async (sessionId: string): Promise<CurrentRecording> => {
  // This page is pinned to the ledger, so an unreadable legacy store means
  // "no legacy recording" rather than a failed resolve.
  const legacy = await db.getCurrentRecording(sessionId).catch(() => undefined);
  const row = await currentLedgerRow(sessionId);

  if (legacy && row) {
    if (legacy.id === row.recordingId) return { world: 'legacy', legacy };
    return startedAt(legacy.startDate) >= row.createdAt
      ? { world: 'legacy', legacy }
      : { world: 'ledger', row };
  }
  if (legacy) return { world: 'legacy', legacy };
  if (row) return { world: 'ledger', row };
  return { world: 'none' };
};

const hasActiveChunkWork = () => {
  const state = useSessionRecordingStore.getState();
  return (
    state.processingChunksIds.length > 0 ||
    (state.captureSlot?.recorder.hasPendingCapture() ?? false) ||
    Object.values(state.chunkRecorders).some((recorder) => recorder.hasChunksInLimbo())
  );
};

const getUploadProgressStoreV2 = async (
  sessionId: string
): Promise<UploadProgressState> => {
  const chunksInLimbo = hasActiveChunkWork();
  const current = await resolveCurrentRecording(sessionId);

  if (current.world === 'ledger') {
    const counts = await ledgerProgress(current.row);
    const isComplete =
      !chunksInLimbo &&
      counts.pending === 0 &&
      // A store that cannot answer must not kill the poll: the counts already
      // came from memory, and "not complete yet" is the safe verdict.
      (await isLedgerUploadComplete(current.row).catch(() => false));
    const progress = isComplete
      ? 100
      : counts.total === 0
        ? 0
        : Math.floor((counts.settled / counts.total) * 100);
    return { isComplete, progress: isComplete ? 100 : Math.min(progress, 99) };
  }

  if (current.world === 'none') {
    return { isComplete: !chunksInLimbo, progress: chunksInLimbo ? 0 : 100 };
  }

  const total = current.legacy.chunks.length;
  const completed = current.legacy.chunks.filter((chunk) => chunk.isProcessed).length;
  const isComplete =
    !chunksInLimbo && current.legacy.chunks.every((chunk) => chunk.isProcessed);
  const progress = isComplete
    ? 100
    : total === 0
      ? 0
      : Math.floor((completed / total) * 100);
  return { isComplete, progress: chunksInLimbo ? Math.min(progress, 99) : progress };
};

/** Preserves the legacy-only read path unless this page is pinned to the ledger. */
export const getUploadProgress = async (
  sessionId: string
): Promise<UploadProgressState> => {
  if (getPipelineMode() === 'ledger') return getUploadProgressStoreV2(sessionId);

  const chunksInLimbo = hasActiveChunkWork();
  const recording = await db.getCurrentRecording(sessionId);
  if (!recording) {
    return { isComplete: !chunksInLimbo, progress: chunksInLimbo ? 0 : 100 };
  }

  const total = recording.chunks.length;
  const completed = recording.chunks.filter((chunk) => chunk.isProcessed).length;
  const isComplete =
    !chunksInLimbo && recording.chunks.every((chunk) => chunk.isProcessed);
  const progress = isComplete
    ? 100
    : total === 0
      ? 0
      : Math.floor((completed / total) * 100);
  return { isComplete, progress: chunksInLimbo ? Math.min(progress, 99) : progress };
};

/** Named so the retry loop's own logs distinguish it from a slow upload. */
const undeliveredChunksError = (): Error => {
  const error = new Error('retained chunks are still draining');
  error.name = 'DrainableChunksError';
  return error;
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (!signal?.aborted) return;
  const error = new Error('Waiting for chunks was aborted');
  error.name = 'AbortError';
  throw error;
};

const waitForChunksStoreV2 = async (sessionId: string, signal?: AbortSignal) => {
  try {
    await pRetry(
      () => {
        throwIfAborted(signal);
        if (hasActiveChunkWork()) {
          throw Error(
            'waiting for all chunks to be received before checking for processing'
          );
        }
      },
      {
        retries: 3,
        signal,
        onFailedAttempt: (err) => {
          logger.warn(`retrying because chunks are in limbo: ${err}`);
        },
      }
    );
  } catch {
    throwIfAborted(signal);
    logger.error({ event: 'WAIT_FOR_CHUNKS_IN_LIMBO_RETRY_LIMIT_REACHED' });
  }

  try {
    await pRetry(
      async () => {
        throwIfAborted(signal);
        // Failed chunks read as settled, so without this the note is generated
        // before the drain this wait poked can deliver them.
        if (hasDrainableRetainedChunks(sessionId)) {
          // One drain settles long before this wait's budget does; re-poke so
          // the chunk spends its dispatch cap instead of expiring unsent.
          if (!isRetryDrainActive()) {
            const { setState, getState } = useSessionRecordingStore;
            pokeRetryDrain(setState, getState, 'note-generation');
          }
          throw undeliveredChunksError();
        }
        // Migration may finish between retries.
        const current = await resolveCurrentRecording(sessionId);
        if (current.world === 'none') return;
        if (current.world === 'ledger') {
          if (!(await isLedgerUploadComplete(current.row))) throw new Error();
          return;
        }
        if (!current.legacy.chunks.every((chunk) => chunk.isProcessed)) {
          throw new Error();
        }
      },
      { minTimeout: 1000, maxTimeout: 5000, retries: 180, signal }
    );
  } catch (err) {
    throwIfAborted(signal);
    // Buffered audio the note will now miss reads exactly like a slow upload
    // without these: the count and the expired gate's error name separate a
    // consult that lost audio from one that merely finished late.
    logger.error({
      event: 'WAIT_FOR_CHUNKS_RETRY_LIMIT_REACHED',
      sessionId,
      details: {
        drainableChunks: countDrainableRetainedChunks(sessionId),
        ...getSafeErrorFields(err),
      },
    });
  }
};

// Wait until all chunks are done processing.
export const waitForChunks = async (sessionId: string, signal?: AbortSignal) => {
  if (getPipelineMode() === 'ledger') {
    // Last moment a retry can still reach the note.
    const { setState, getState } = useSessionRecordingStore;
    pokeRetryDrain(setState, getState, 'note-generation');
    return waitForChunksStoreV2(sessionId, signal);
  }
  try {
    await pRetry(
      () => {
        throwIfAborted(signal);
        const { chunkRecorders } = useSessionRecordingStore.getState();
        const chunksInLimbo = values(chunkRecorders).some((recorder) =>
          recorder.hasChunksInLimbo()
        );

        if (chunksInLimbo) {
          throw new Error(
            'waiting for all chunks to be received before checking for processing'
          );
        }
      },
      {
        retries: 3,
        signal,
        onFailedAttempt: (error) => {
          logger.warn(`retrying because chunks are in limbo: ${error}`);
        },
      }
    );
  } catch {
    throwIfAborted(signal);
    logger.error({ event: 'WAIT_FOR_CHUNKS_IN_LIMBO_RETRY_LIMIT_REACHED' });
  }

  try {
    await pRetry(
      async () => {
        throwIfAborted(signal);
        const recording = await db.getCurrentRecording(sessionId);
        if (!recording) return;
        if (!recording.chunks.every((chunk) => chunk.isProcessed)) throw new Error();
      },
      { minTimeout: 1000, maxTimeout: 5000, retries: 180, signal }
    );
  } catch {
    throwIfAborted(signal);
    logger.error({ event: 'WAIT_FOR_CHUNKS_RETRY_LIMIT_REACHED' });
  }
};
